import "@fontsource/montserrat";
import "@fontsource/montserrat/300.css";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/700.css";
import {
  createTheme,
  CssBaseline,
  GlobalStyles,
  type PaletteColorOptions,
  responsiveFontSizes,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material";
import { deDE, enUS, type Localization } from "@mui/material/locale";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

/** Primary colors */
const primary = {
  main: '#96b721',
  dark: '#000000',
  light: '#b5d44a',
  contrastText: '#ffffff',
};

/** Secondary colors */
const secondary = {
  main: '#4a5568',
  dark: '#2d3748',
  light: '#718096',
  contrastText: '#ffffff',
};

/** Accent/error colors */
const error = {
  main: '#e84127',
  dark: '#c41d05',
  light: '#ff6f4e',
  contrastText: '#ffffff',
};

/** Accent/warning colors */
const warning = {
  main: '#ffed00',
  dark: '#ccbe00',
  light: '#fff133',
  contrastText: '#000000',
};

const localizations: Record<string, Localization> = {
  de: deDE,
  en: enUS,
};

function useLocalization() {
  const { i18n } = useTranslation();
  return localizations[i18n.language];
}

function useDefineTheme(primaryColor: PaletteColorOptions = primary) {
  const localization = useLocalization();

  return useMemo(
    () =>
      responsiveFontSizes(
        createTheme(
          {
            palette: {
              primary: primaryColor,
              secondary,
              error,
              warning,
              background: {
                default: "#ffffff",
              },
            },
            typography: {
              fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
              fontSize: 14,
              fontWeightLight: 300,
              fontWeightRegular: 400,
              fontWeightMedium: 500,
              fontWeightBold: 700,
            },
          },
          localization
        )
      ),
    [primaryColor, localization]
  );
}

/** The root theme provider that applies the default theme and CSS reset. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeWrapper primary={primary}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          "#root": {
            minHeight: "100vh",
            overflow: "scroll-y",
            display: "flex",
            flexDirection: "column",
          },
        }}
      />
      {children}
    </ThemeWrapper>
  );
}

/** With this wrapper you can apply another primary color to any part of the application. */
export function ThemeWrapper({
  children,
  primary,
}: {
  children?: ReactNode;
  primary: PaletteColorOptions;
}) {
  const theme = useDefineTheme(primary);

  return (
    <MuiThemeProvider theme={theme}>{children ?? <Outlet />}</MuiThemeProvider>
  );
}
